import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../api';

/**
 * Hook para injetar Google Analytics e Google Tag Manager
 * Busca os IDs configurados no backend e injeta os scripts
 */
export const useAnalytics = () => {
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        // Buscar configurações de SEO que incluem os IDs de analytics (rota pública)
        const response = await api.get('/seo/config-public');
        const config = response.data?.data;

        // Injetar Google Analytics (GA4)
        if (config?.google_analytics_id) {
          injectGoogleAnalytics(config.google_analytics_id);
        }

        // Injetar Google Tag Manager
        if (config?.google_tag_manager_id) {
          injectGoogleTagManager(config.google_tag_manager_id);
        }

        // Injetar Facebook Pixel
        if (config?.facebook_pixel_id) {
          injectFacebookPixel(config.facebook_pixel_id);
        }
      } catch (error) {
        console.log('Analytics config not available');
      }
    };

    loadAnalytics();
  }, []);
};

/**
 * Injeta Google Analytics (GA4) na página
 */
const injectGoogleAnalytics = (gaId) => {
  // Evitar injeção duplicada
  if (window.gtag) return;

  const scriptId = 'google-analytics';
  if (document.getElementById(scriptId)) return;

  // Criar tag para Google Analytics
  const script = document.createElement('script');
  script.id = scriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  // Inicializar gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', gaId, {
    page_path: window.location.pathname,
  });
};

/**
 * Injeta Google Tag Manager na página
 */
const injectGoogleTagManager = (gtmId) => {
  // Evitar injeção duplicada
  if (document.getElementById('google-tag-manager')) return;

  // Script do GTM
  const script = document.createElement('script');
  script.id = 'google-tag-manager';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  // Inicializar dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // NoScript fallback
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
};

/**
 * Injeta Facebook Pixel na página
 */
const injectFacebookPixel = (pixelId) => {
  // Evitar injeção duplicada
  if (window.fbq) return;

  const script = document.createElement('script');
  script.id = 'facebook-pixel';
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // NoScript fallback
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = '1';
  img.width = '1';
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.insertBefore(noscript, document.body.firstChild);
};

/**
 * Componente para injetar scripts de análise com React Helmet
 */
export const AnalyticsHelmet = ({ gaId, gtmId, pixelId }) => {
  return (
    <Helmet>
      {/* Google Analytics */}
      {gaId && (
        <>
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
          ></script>
          <script>{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaId}');
          `}</script>
        </>
      )}

      {/* Google Tag Manager */}
      {gtmId && (
        <script>{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `}</script>
      )}

      {/* Facebook Pixel */}
      {pixelId && (
        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `}</script>
      )}
    </Helmet>
  );
};

/**
 * Track de eventos customizados para Google Analytics
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

/**
 * Track de conversão no Facebook Pixel
 */
export const trackPixelEvent = (eventName, eventData = {}) => {
  if (window.fbq) {
    window.fbq('track', eventName, eventData);
  }
};
